import React, { Fragment, useContext, useEffect, useState } from "react";
import { Grid, Divider, TextField, Button } from "@material-ui/core";
import { ToggleButtonGroup, ToggleButton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import DateFnsUtils from "@date-io/date-fns";

import moment from "moment";
import MomentUtils from "@date-io/moment";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
  DatePicker,
} from "@material-ui/pickers";

import ReportContext from "../../../context/reports/ReportContext";
import ParameterContext from "../../../context/parameters/ParameterContext";

const useStyles = makeStyles(() => ({
  action: {
    backgroundColor: "white",
    marginLeft: 5,
    marginRight: 5,
  },
}));

const ReportActions = () => {
  const classes = useStyles();

  const { owner, setOwner, setDateRange, reports_loading, getReportsByDate } =
    useContext(ReportContext);
  const { owner_store, getOwnerStore } = useContext(ParameterContext);

  const period_picker = [
    "Today",
    "Yesterday",
    "Last 7 Days",
    "This Month",
    "Last Month",
  ];
  const [periodSelected, setPeriodSelected] = useState("Today");
  const [dateIndex, setDateIndex] = useState(null);
  const [dateFromSelected, setDateFromSelected] = useState(null);
  const [dateToSelected, setDateToSelected] = useState(null);

  const onToday = () => {
    setDateFromSelected(moment());
    setDateToSelected(moment().add(1, "days"));

    const date_from = moment().format("YYYY-MM-DD");
    const date_to = moment().add(1, "days").format("YYYY-MM-DD");
    const date_index = date_from + " " + date_to + " " + owner;

    if (dateIndex !== date_index) {
      getReportsByDate(date_from, date_to, "today");
      setDateRange(date_from + " - " + date_to);
      setDateIndex(date_index);
    }
  };

  const onYesterday = () => {
    setDateFromSelected(moment().subtract(1, "days"));
    setDateToSelected(moment());

    const date_from = moment().subtract(1, "days").format("YYYY-MM-DD");
    const date_to = moment().format("YYYY-MM-DD");
    const date_index = date_from + " " + date_to + " " + owner;

    if (dateIndex !== date_index) {
      getReportsByDate(date_from, date_to, "yesterday");
      setDateRange(date_from + " - " + date_to);
      setDateIndex(date_index);
    }
  };

  const onLastSevenDays = () => {
    setDateFromSelected(moment().subtract(7, "days"));
    setDateToSelected(moment().add(1, "days"));

    const date_from = moment().subtract(7, "days").format("YYYY-MM-DD");
    const date_to = moment().add(1, "days").format("YYYY-MM-DD");
    const date_index = date_from + " " + date_to + " " + owner;

    if (dateIndex !== date_index) {
      getReportsByDate(date_from, date_to, "lastSevenDay");
      setDateRange(date_from + " - " + date_to);
      setDateIndex(date_index);
    }
  };

  const onThisMonth = () => {
    setDateFromSelected(moment().startOf("month"));
    setDateToSelected(moment().add(1, "days"));

    const date_from = moment().startOf("month").format("YYYY-MM-DD");
    const date_to = moment().add(1, "days").format("YYYY-MM-DD");
    const date_index = date_from + " " + date_to + " " + owner;

    if (dateIndex !== date_index) {
      getReportsByDate(date_from, date_to, "thisMonth");
      setDateRange(date_from + " - " + date_to);
      setDateIndex(date_index);
    }
  };

  const onLastMonth = () => {
    setDateFromSelected(moment().subtract(1, "months").startOf("month"));
    setDateToSelected(moment().subtract(1, "months").endOf("month"));

    const date_from = moment()
      .subtract(1, "months")
      .startOf("month")
      .format("YYYY-MM-DD");
    const date_to = moment()
      .subtract(1, "months")
      .endOf("month")
      .format("YYYY-MM-DD");
    const date_index = date_from + " " + date_to + " " + owner;

    if (dateIndex !== date_index) {
      getReportsByDate(date_from, date_to, "lastMonth");
      setDateRange(date_from + " - " + date_to);
      setDateIndex(date_index);
    }
  };

  const onDateSearch = () => {
    const date_from = moment(dateFromSelected).format("YYYY-MM-DD");
    const date_to = moment(dateToSelected).format("YYYY-MM-DD");

    // Auto-changed dropdown value and retrieve report based on dropdown value
    let periodSelected = "";

    const today = moment().format("YYYY-MM-DD");
    const next_day = moment().add(1, "days").format("YYYY-MM-DD");
    const prev_one = moment().subtract(1, "days").format("YYYY-MM-DD");
    const prev_seven = moment().subtract(7, "days").format("YYYY-MM-DD");
    const this_start = moment().startOf("month").format("YYYY-MM-DD");
    const prev_start = moment()
      .subtract(1, "months")
      .startOf("month")
      .format("YYYY-MM-DD");
    const prev_end = moment()
      .subtract(1, "months")
      .endOf("month")
      .format("YYYY-MM-DD");

    if (moment(date_from).isSame(today) && moment(date_to).isSame(next_day)) {
      periodSelected = "Today";
    } else if (
      moment(date_from).isSame(prev_one) &&
      moment(date_to).isSame(today)
    ) {
      periodSelected = "Yesterday";
    } else if (
      moment(date_from).isSame(prev_seven) &&
      moment(date_to).isSame(next_day)
    ) {
      periodSelected = "Last 7 Days";
    } else if (
      moment(date_from).isSame(this_start) &&
      moment(date_to).isSame(next_day)
    ) {
      periodSelected = "This Month";
    } else if (
      moment(date_from).isSame(prev_start) &&
      moment(date_to).isSame(prev_end)
    ) {
      periodSelected = "Last Month";
    }

    setPeriodSelected(periodSelected);

    // Retrieve Reports based on date selected
    if (!periodSelected) {
      const date_index = date_from + " " + date_to + " " + owner;
      const text = moment(date_to).isSameOrAfter(today)
        ? "custom_today"
        : "custom_date";

      if (dateIndex !== date_index) {
        getReportsByDate(date_from, date_to, text);
        setDateRange(date_from + " - " + date_to);
        setDateIndex(date_index);
      }
    }
  };

  useEffect(() => {
    switch (periodSelected) {
      case "Today":
        onToday();
        break;

      case "Yesterday":
        onYesterday();
        break;

      case "Last 7 Days":
        onLastSevenDays();
        break;

      case "This Month":
        onThisMonth();
        break;

      case "Last Month":
        onLastMonth();
        break;

      default:
        onDateSearch();
        break;
    }
  }, [periodSelected, owner]);

  useEffect(() => {
    getOwnerStore();
  }, []);

  return (
    <Fragment>
      <Grid item xs={12}>
        <Divider variant="fullWidth" />
      </Grid>
      <Grid
        item
        xs={12}
        container
        spacing={1}
        justifyContent="space-between"
        alignItems="center"
        className={classes.action}
      >
        {/* <Grid item>
          <TextField
            required
            size="small"
            margin="dense"
            variant="outlined"
            select
            SelectProps={{
              native: true,
            }}
            id="period"
            name="period"
            onChange={(e) => setPeriodSelected(e.target.value)}
            value={periodSelected}
          >
            <option value="" disabled />
            {period_picker.map((each) => (
              <option key={each} value={each}>
                {each}
              </option>
            ))}
          </TextField>
        </Grid> */}
        <Grid
          item
          xs
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <DatePicker
              disableToolbar
              variant="inline"
              format="yyyy-MM-dd"
              margin="dense"
              id="date_from"
              label="From"
              disabled={reports_loading}
              autoOk={true}
              value={dateFromSelected}
              onChange={(date) => setDateFromSelected(date)}
              style={{
                marginLeft: 5,
                marginRight: 10,
              }}
            />
          </MuiPickersUtilsProvider>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <DatePicker
              disableToolbar
              variant="inline"
              format="yyyy-MM-dd"
              margin="dense"
              id="date_to"
              label="To"
              disabled={reports_loading}
              autoOk={true}
              value={dateToSelected}
              onChange={(date) => setDateToSelected(date)}
              style={{
                marginRight: 10,
              }}
            />
          </MuiPickersUtilsProvider>
          <Button
            variant="contained"
            color="primary"
            onClick={onDateSearch}
            disabled={
              reports_loading ||
              moment(dateFromSelected).format("YYYY-MM-DD") >
                moment(dateToSelected).format("YYYY-MM-DD")
            }
          >
            Search
          </Button>
        </Grid>
        <Grid item>
          <ToggleButtonGroup
            exclusive
            size="small"
            variant="outlined"
            value={owner}
            onChange={(e, value) => setOwner(value ? value : owner)}
          >
            {owner_store &&
              owner_store.map((owner, index) => (
                <ToggleButton key={index} value={owner}>
                  {owner}
                </ToggleButton>
              ))}
          </ToggleButtonGroup>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider variant="fullWidth" />
      </Grid>
    </Fragment>
  );
};

export default ReportActions;
